import { faLinkHorizontal } from "@fullscript/pro-solid-svg-icons";
import { $isLinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $isAtNodeEnd } from "@lexical/selection";
import { mergeRegister } from "@lexical/utils";
import type { RangeSelection } from "lexical";
import { $getSelection, $isRangeSelection } from "lexical";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { l } from "@aviary/locales/i18n";

import { ToolbarButton } from "../ToolbarButton";

import { LinkInput } from "./LinkInput";

/**
 * @deprecated DS4 component — DS5 support is in progress. Do not add new usages.
 */
const LinkButton = () => {
  const { t } = useTranslation();

  const [editor] = useLexicalComposerContext();

  const [isLink, setIsLink] = useState(false);

  const getSelectedNode = (selection: RangeSelection | null) => {
    const anchor = selection?.anchor;
    const focus = selection?.focus;
    const anchorNode = anchor?.getNode();
    const focusNode = focus?.getNode();
    if (anchorNode === focusNode) {
      return anchorNode;
    }
    const isBackward = selection!.isBackward();

    // selection with mouse from right to left
    if (isBackward) {
      return $isAtNodeEnd(focus!) ? anchorNode : focusNode;
    } else {
      return $isAtNodeEnd(anchor!) ? focusNode : anchorNode;
    }
  };

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();

    const node = getSelectedNode($isRangeSelection(selection) ? selection : null);

    const parent = node?.getParent();
    if ($isLinkNode(parent) || $isLinkNode(node)) {
      setIsLink(true);
    } else {
      setIsLink(false);
    }
  }, []);

  // looks/reads the current state of the editor, and calls updateToolbar when the RTE has edits
  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar();
        });
      })
    );
  }, [updateToolbar, editor]);

  const insertLink = useCallback(() => {
    if (!isLink) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, {
        url: "https://",
      });
    } else {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    }
  }, [editor, isLink]);

  return (
    <>
      <ToolbarButton
        icon={faLinkHorizontal}
        isActive={isLink}
        onClick={insertLink}
        aria-label={t(l.aviary.richTextEditor.link)}
      />
      <LinkInput isLinkSelected={isLink} />
    </>
  );
};

export { LinkButton };
